import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.dark.bg};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const StatusCard = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xxl};
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ status, theme }) => {
    if (status === 'success') return theme.colors.success[500] + '20';
    if (status === 'error') return theme.colors.error[500] + '20';
    return theme.colors.primary[500] + '20';
  }};
  
  svg {
    width: 40px;
    height: 40px;
    color: ${({ status, theme }) => {
      if (status === 'success') return theme.colors.success[500];
      if (status === 'error') return theme.colors.error[500];
      return theme.colors.primary[500];
    }};
    
    ${({ status }) => status === 'processing' && `
      animation: spin 1s linear infinite;
    `}
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const Message = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  line-height: 1.6;
`;

const ServiceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary[500]}20;
  color: ${({ theme }) => theme.colors.primary[400]};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const RedirectMessage = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textTertiary};
  margin: ${({ theme }) => theme.spacing.lg} 0 0;
`;

const OAuthCallback = () => {
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [service, setService] = useState('');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const processingRef = useRef(false);  // Prevent duplicate processing

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate processing
      if (processingRef.current) {
        console.log('⏭️  Callback already being processed, skipping...');
        return;
      }
      
      processingRef.current = true;
      
      try {
        console.log('🔄 OAuth Callback - Starting...');
        console.log('   URL:', location.search);
        
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');

        console.log('   Code (first 20 chars):', code ? code.substring(0, 20) + '...' : 'MISSING');
        console.log('   State:', state || 'MISSING');

        if (!code || !state) {
          console.error('❌ Missing code or state');
          setStatus('error');
          setErrorMessage('Missing authorization code or state parameter');
          return;
        }

        // Extract service name from state (format: "connect:gmail:user_id:...")
        const stateParts = state.split(':');
        if (stateParts.length >= 2) {
          const serviceName = stateParts[1];
          setService(serviceName);
        }

        // Send callback to backend
        const backendURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        console.log('📤 Sending to backend:', `${backendURL}/api/oauth/callback`);
        console.log('   Payload:', { code: code.substring(0, 20) + '...', state });
        
        const response = await fetch(`${backendURL}/api/oauth/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state })
        });

        console.log('📥 Backend response status:', response.status);
        const data = await response.json();
        console.log('📥 Backend response data:', data);

        if (response.ok && data.success) {
          console.log('✅ Connection successful!');
          setStatus('success');
          setService(data.service);
          setEmail(data.service_email);
          
          // Redirect to connections page after 2 seconds
          setTimeout(() => {
            navigate('/connections');
          }, 2000);
        } else {
          setStatus('error');
          setErrorMessage(data.detail || 'Failed to complete connection');
          
          // Redirect to connections page after 4 seconds
          setTimeout(() => {
            navigate('/connections');
          }, 4000);
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setErrorMessage('An unexpected error occurred');
        
        // Redirect to connections page after 4 seconds
        setTimeout(() => {
          navigate('/connections');
        }, 4000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  const getServiceDisplayName = (serviceName) => {
    const serviceNames = {
      gmail: 'Gmail',
      drive: 'Google Drive',
      calendar: 'Google Calendar'
    };
    return serviceNames[serviceName] || serviceName;
  };

  return (
    <CallbackContainer>
      <StatusCard>
        <IconWrapper status={status}>
          {status === 'processing' && <Loader />}
          {status === 'success' && <CheckCircle />}
          {status === 'error' && <XCircle />}
        </IconWrapper>

        {status === 'processing' && (
          <>
            <Title>Connecting {service ? getServiceDisplayName(service) : 'Service'}...</Title>
            <Message>
              Please wait while we complete your connection.
            </Message>
          </>
        )}

        {status === 'success' && (
          <>
            <Title>Successfully Connected!</Title>
            {service && (
              <ServiceBadge>
                {getServiceDisplayName(service)}
              </ServiceBadge>
            )}
            <Message>
              Your {service ? getServiceDisplayName(service) : 'service'} account has been connected.
              {email && (
                <>
                  <br />
                  <strong>{email}</strong>
                </>
              )}
            </Message>
            <RedirectMessage>
              Redirecting to connections page...
            </RedirectMessage>
          </>
        )}

        {status === 'error' && (
          <>
            <Title>Connection Failed</Title>
            <Message>
              {errorMessage || 'We couldn\'t complete your connection. Please try again.'}
            </Message>
            <RedirectMessage>
              Redirecting to connections page...
            </RedirectMessage>
          </>
        )}
      </StatusCard>
    </CallbackContainer>
  );
};

export default OAuthCallback;
